import { useState, useMemo, memo } from "react";
import type { Context } from "common/types/context";
import type { EntityColumn } from "common/entities/entity-column/types";
import { LabelWidget } from "common/form/widget/label-widget";
import { Modal } from "common/widgets/modal";
import { ShowMore } from "common/widgets/show-more";

const PAGE_SIZE = 25;

interface RecordListSummaryProps {
  context: Context;
  entityName: string;
  recordIds: string[];
  recordsCount: number;
  entityColumn: EntityColumn;
  title: string;
  hideLinks?: boolean;
}

export const RecordListSummary = memo(
  ({
    context,
    entityName,
    recordsCount,
    entityColumn,
    title,
    recordIds = [],
    hideLinks = false,
  }: RecordListSummaryProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [visibleRecords, setVisibleRecords] = useState(PAGE_SIZE);

    const toggleModal = () => setIsModalOpen(!isModalOpen);

    const handleShowMore = () => {
      setVisibleRecords((prev) => Math.min(prev + PAGE_SIZE, recordIds.length));
    };

    const visibleRecordIds = useMemo(
      () => recordIds.slice(0, visibleRecords),
      [recordIds, visibleRecords],
    );

    return (
      <div className="x-record-list-summary">
        <div className="x-record-list-summary-cell">
          {recordIds.length > 0 ? (
            <span className="x-summary-sample">
              <LabelWidget
                withLinks={!hideLinks}
                context={context}
                entityName={entityName}
                column={entityColumn}
                value={recordIds[0]}
              />
            </span>
          ) : undefined}
          {recordsCount > 1 ? (
            <button className="x-summary-more" onClick={toggleModal}>
              {`+${recordsCount - 1}`}
            </button>
          ) : undefined}
        </div>
        {isModalOpen ? (
          <Modal
            className="x-record-list-summary-modal"
            title={title}
            content={
              <div className="x-record-list-content-wrapper">
                <div className="x-record-list-records">
                  {visibleRecordIds.map((recordId) => (
                    <LabelWidget
                      key={recordId}
                      withLinks={!hideLinks}
                      context={context}
                      entityName={entityName}
                      column={entityColumn}
                      value={recordId}
                    />
                  ))}
                </div>
                {visibleRecords < recordIds.length ? (
                  <div className="x-record-list-footer">
                    <ShowMore action={handleShowMore} />
                  </div>
                ) : undefined}
              </div>
            }
            hideFooter={true}
            onCancel={toggleModal}
          />
        ) : undefined}
      </div>
    );
  },
);

RecordListSummary.displayName = "RecordListSummary";
